import React, { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { CONTACT } from "../lib/site";
import BookingAssistant from "./BookingAssistant";
import {
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Globe2,
  Loader2,
  Mail,
  MapPin,
  MessageCircle,
  Music4,
  Phone,
  Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

const EVENT_TYPES = [
  {
    value: "Rubai — Haldi Morning",
    label: "Haldi / Mehendi",
    note: "Bright daytime format with folk vocals and lighter percussion.",
  },
  {
    value: "Rubai — Cocktail Sundowner",
    label: "Cocktail Party",
    note: "Sufi lounge crossover with premium evening energy.",
  },
  {
    value: "Rubai — Wedding Ceremony",
    label: "Wedding Ceremony",
    note: "Ceremonial scoring for key wedding rituals and transitions.",
  },
  {
    value: "Rubai — Reception Night",
    label: "Reception Night",
    note: "Premium live band format for the final celebration.",
  },
  {
    value: "Rubai + Production",
    label: "Band + Production",
    note: "Live music bundled with sound, lights and LED support.",
  },
  {
    value: "Production Only",
    label: "Sound / Lights / LED",
    note: "Technical production for weddings and premium events.",
  },
  {
    value: "Destination / International",
    label: "Destination Wedding",
    note: "Travel-ready ensemble for global celebrations.",
  },
];

const PREFERRED_CITIES = ["Delhi", "Jaipur", "Udaipur", "Goa", "Dubai", "London"];

const CONCIERGE_POINTS = [
  {
    icon: Clock3,
    eyebrow: "Fast reply",
    title: "Availability in 24 hours",
    copy: "Tell us your date, city and event flow. We come back with a real response, not an auto-template.",
  },
  {
    icon: Music4,
    eyebrow: "Curated score",
    title: "Proposal shaped around your rituals",
    copy: "We tailor the lineup, pacing and sound palette around your celebration instead of forcing a fixed package.",
  },
  {
    icon: Globe2,
    eyebrow: "Travel ready",
    title: "India and destination weddings",
    copy: "From Delhi to Dubai to Europe, the booking flow already accounts for travel, logistics and stage needs.",
  },
];

const NEXT_STEPS = [
  {
    step: "01",
    title: "Share the essentials",
    copy: "Date, city, guest size and the ceremony you want scored first.",
  },
  {
    step: "02",
    title: "Receive a tailored proposal",
    copy: "We respond with availability, lineup direction and the right pricing conversation for your celebration.",
  },
  {
    step: "03",
    title: "Lock the performance",
    copy: "Once aligned, we hold the date and move into music planning.",
  },
];

const initialState = {
  full_name: "",
  email: "",
  phone: "",
  event_type: "",
  event_date: "",
  location: "",
  international: false,
  guest_count: "",
  budget: "Discuss on call",
  message: "",
};

export const Bookings = () => {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (key) => (event) => {
    const value =
      event?.target?.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [key]: value }));
  };

  const setField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const phone = CONTACT.phone.replace(/[^\d+]/g, "");
  const whatsappPhone = CONTACT.whatsapp.replace(/[^\d+]/g, "").replace(/^\+/, "");
  const whatsappPreset = encodeURIComponent(
    "Hi AB Events, I want to check availability for my event. Could you share the next steps?"
  );
  const waLink = `https://wa.me/${whatsappPhone}?text=${whatsappPreset}`;
  const telLink = `tel:${phone}`;
  const mailLink = `mailto:${CONTACT.email}?subject=${encodeURIComponent(
    "AB Events Booking Enquiry"
  )}`;

  const submit = async (event) => {
    event.preventDefault();

    if (
      !form.full_name ||
      !form.email ||
      !form.phone ||
      !form.event_type ||
      !form.location
    ) {
      toast.error("Please fill in the required details first.");
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/bookings`, form);
      setSuccess(true);
      toast.success("Enquiry received. We will reply with availability within 24 hours.");
      setForm(initialState);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Could not submit right now. Please try again or contact us directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="bookings-section"
      className="relative overflow-hidden bg-[#070707] py-24 md:py-32 lg:py-40"
    >
      <Toaster theme="dark" position="top-center" richColors />

      <div className="pointer-events-none absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-[#d4af37]/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 h-[420px] w-[420px] rounded-full bg-[#b8860b]/10 blur-3xl" />

      <div className="relative mx-auto max-w-[1440px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 items-start gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="overline">— Bookings</div>
            <h2 className="mt-6 font-serif text-4xl font-light leading-[1.04] md:text-5xl lg:text-6xl">
              A more personal <em className="gold-gradient-text not-italic">booking concierge</em> for your celebration.
            </h2>
            <p className="mt-7 max-w-xl text-base font-light leading-relaxed text-white/65 md:text-lg">
              This section is built for premium enquiries. Share the essentials,
              or skip straight to WhatsApp if you already know the date and city.
            </p>
            <div className="mt-5 inline-flex rounded-full border border-[#d4af37]/25 bg-[#d4af37]/8 px-4 py-2 text-[11px] uppercase tracking-[0.28em] text-[#d4af37]">
              Pricing discussed personally on call
            </div>

            <div className="mt-10 grid gap-4 md:grid-cols-3 lg:grid-cols-1">
              {CONCIERGE_POINTS.map((point) => {
                const Icon = point.icon;
                return (
                  <div
                    key={point.title}
                    className="border border-white/10 bg-white/[0.03] p-5 backdrop-blur-sm"
                  >
                    <div className="flex items-start gap-4">
                      <div className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#d4af37]/35 bg-[#d4af37]/8 text-[#d4af37]">
                        <Icon size={18} />
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">
                          {point.eyebrow}
                        </div>
                        <div className="mt-2 font-serif text-2xl font-light text-white">
                          {point.title}
                        </div>
                        <p className="mt-2 text-sm font-light leading-relaxed text-white/55">
                          {point.copy}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="gold-hairline my-10 max-w-sm" />

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
              <QuickAction
                href={waLink}
                target="_blank"
                rel="noreferrer"
                icon={MessageCircle}
                eyebrow="Fastest route"
                title="WhatsApp concierge"
                detail="Share your date and city now"
                testId="booking-whatsapp"
                accent="bg-[#25D366] text-white border-[#25D366]"
              />
              <QuickAction
                href={telLink}
                icon={Phone}
                eyebrow="Direct call"
                title={CONTACT.phone}
                detail="Talk through availability"
                testId="booking-call"
              />
              <QuickAction
                href={mailLink}
                icon={Mail}
                eyebrow="Email"
                title={CONTACT.email}
                detail="Send venue and ceremony details"
                testId="booking-email"
              />
            </div>

            <div className="mt-10 border border-white/10 bg-[linear-gradient(180deg,rgba(212,175,55,0.08)_0%,rgba(11,9,7,0.9)_100%)] p-6">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#d4af37]">
                What happens next
              </div>
              <div className="mt-5 space-y-5">
                {NEXT_STEPS.map((item) => (
                  <div
                    key={item.step}
                    className="grid grid-cols-[50px_1fr] gap-4 border-t border-white/8 pt-5 first:border-t-0 first:pt-0"
                  >
                    <div className="font-serif text-2xl text-[#d4af37]">{item.step}</div>
                    <div>
                      <div className="font-serif text-2xl font-light text-white">
                        {item.title}
                      </div>
                      <p className="mt-2 text-sm font-light leading-relaxed text-white/55">
                        {item.copy}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="relative lg:col-span-7">
            <BookingAssistant whatsappHref={waLink} callHref={telLink} />

            {success ? (
              <div
                data-testid="booking-success"
                className="border border-[#d4af37]/40 bg-[#101010] p-10 text-center md:p-14"
              >
                <CheckCircle2 size={48} className="mx-auto text-[#d4af37]" />
                <h3 className="mt-6 font-serif text-3xl md:text-4xl">
                  Enquiry received.
                </h3>
                <p className="mx-auto mt-4 max-w-md text-white/65">
                  Thank you for choosing AB Events. We will come back with
                  availability, lineup direction and next steps within 24 hours.
                </p>

                <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                  <button
                    data-testid="booking-new-btn"
                    onClick={() => setSuccess(false)}
                    className="outline-button rounded-sm px-8 py-3 text-xs uppercase tracking-[0.25em]"
                  >
                    Submit another
                  </button>
                  <a
                    href={waLink}
                    target="_blank"
                    rel="noreferrer"
                    className="gold-button inline-flex items-center justify-center gap-2 rounded-sm px-8 py-3 text-xs font-semibold uppercase tracking-[0.25em]"
                  >
                    WhatsApp concierge <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            ) : (
              <div className="border border-white/10 bg-[#0d0d0d]/85 backdrop-blur">
                <div className="grid gap-6 border-b border-white/10 p-6 md:grid-cols-[1.4fr_1fr] md:p-8">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.34em] text-[#d4af37]">
                      Concierge Request
                    </div>
                    <h3 className="mt-3 font-serif text-3xl font-light text-white md:text-4xl">
                      Check your date, city and celebration style.
                    </h3>
                    <p className="mt-3 max-w-xl text-sm font-light leading-relaxed text-white/55 md:text-base">
                      Fill in the form for a tailored proposal, or use the quick
                      actions if you want a faster conversation first.
                    </p>
                  </div>

                  <div className="grid gap-3 text-sm text-white/65 sm:grid-cols-2 md:grid-cols-1">
                    <MiniTrust
                      icon={CalendarDays}
                      title="Now booking 2026 - 2027"
                      detail="Dates fill quickly around peak wedding weekends."
                    />
                    <MiniTrust
                      icon={MapPin}
                      title="India + worldwide"
                      detail="Venue, travel and stage planning accounted for."
                    />
                  </div>
                </div>

                <div className="grid gap-5 border-b border-white/10 bg-white/[0.02] p-6 md:grid-cols-[1.1fr_0.9fr] md:p-8">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.34em] text-[#d4af37]">
                      Quick date check
                    </div>
                    <p className="mt-3 max-w-xl text-sm font-light leading-relaxed text-white/58 md:text-base">
                      If you already know your wedding date and city, jump
                      straight to WhatsApp and we will answer with real
                      availability faster.
                    </p>
                  </div>

                  <div className="flex flex-col items-start justify-center gap-3 md:items-end">
                    <a
                      href={waLink}
                      target="_blank"
                      rel="noreferrer"
                      className="gold-button inline-flex items-center gap-2 rounded-sm px-6 py-3 text-xs font-semibold uppercase tracking-[0.3em]"
                    >
                      WhatsApp fast path <ArrowRight size={14} />
                    </a>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-white/40">
                      Best for quick date checks and first availability.
                    </div>
                  </div>
                </div>

                <form
                  data-testid="booking-form"
                  onSubmit={submit}
                  className="space-y-8 p-6 md:p-8 lg:p-10"
                >
                  <Field
                    label="Preferred city"
                    hint="Tap a city to prefill the location field, or type your venue below"
                  >
                    <div className="flex flex-wrap gap-2">
                      {PREFERRED_CITIES.map((city) => (
                        <ChoicePill
                          key={city}
                          selected={form.location === city}
                          onClick={() => setField("location", city)}
                        >
                          {city}
                        </ChoicePill>
                      ))}
                    </div>
                  </Field>

                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <Field label="Full Name *">
                      <input
                        data-testid="input-full-name"
                        type="text"
                        required
                        value={form.full_name}
                        onChange={update("full_name")}
                        className={inputCls}
                        placeholder="Your name"
                      />
                    </Field>
                    <Field label="Email *">
                      <input
                        data-testid="input-email"
                        type="email"
                        required
                        value={form.email}
                        onChange={update("email")}
                        className={inputCls}
                        placeholder="you@example.com"
                      />
                    </Field>
                    <Field label="Phone / WhatsApp *">
                      <input
                        data-testid="input-phone"
                        type="tel"
                        required
                        value={form.phone}
                        onChange={update("phone")}
                        className={inputCls}
                        placeholder="+91 ..."
                      />
                    </Field>
                    <Field label="Event Date">
                      <input
                        data-testid="input-event-date"
                        type="date"
                        value={form.event_date}
                        onChange={update("event_date")}
                        className={`${inputCls} [color-scheme:dark]`}
                      />
                    </Field>
                    <Field label="Location / Venue *" hint="City, country or venue name">
                      <input
                        data-testid="input-location"
                        type="text"
                        required
                        value={form.location}
                        onChange={update("location")}
                        className={inputCls}
                        placeholder="Delhi, Dubai, Udaipur..."
                      />
                    </Field>
                    <Field label="Guest Count" hint="Optional, but helpful for planning">
                      <input
                        data-testid="input-guest-count"
                        type="text"
                        value={form.guest_count}
                        onChange={update("guest_count")}
                        className={inputCls}
                        placeholder="250 guests"
                      />
                    </Field>
                  </div>

                  <div className="space-y-4 border-t border-white/10 pt-8">
                    <Field
                      label="What are we scoring? *"
                      hint="Choose the celebration that fits best"
                    >
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {EVENT_TYPES.map((item) => (
                          <ChoiceCard
                            key={item.value}
                            selected={form.event_type === item.value}
                            title={item.label}
                            note={item.note}
                            onClick={() => setField("event_type", item.value)}
                          />
                        ))}
                      </div>
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 gap-6 border-t border-white/10 pt-8 md:grid-cols-[1.2fr_0.8fr]">
                    <Field
                      label="Pricing"
                      hint="We align the right format, city, production scope and lineup with you personally."
                    >
                      <div className="border border-[#d4af37]/18 bg-[linear-gradient(180deg,rgba(212,175,55,0.08)_0%,rgba(12,10,8,0.92)_100%)] p-5">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">
                          Concierge note
                        </div>
                        <div className="mt-3 font-serif text-3xl font-light text-white">
                          Discuss on call
                        </div>
                        <p className="mt-3 max-w-lg text-sm font-light leading-relaxed text-white/55">
                          Every celebration is shaped differently, so we keep pricing as a personal conversation instead of locking you into public ranges.
                        </p>
                      </div>
                    </Field>

                    <Field label="Travel Scope">
                      <button
                        data-testid="input-international"
                        type="button"
                        onClick={() =>
                          setField("international", !form.international)
                        }
                        className={`flex min-h-[92px] w-full items-center justify-between border px-5 py-4 text-left transition-all duration-300 ${
                          form.international
                            ? "border-[#d4af37] bg-[#d4af37]/10 text-white"
                            : "border-white/10 bg-black/20 text-white/70 hover:border-[#d4af37]/45 hover:text-white"
                        }`}
                      >
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.28em] text-[#d4af37]">
                            Destination
                          </div>
                          <div className="mt-2 font-serif text-2xl font-light">
                            {form.international ? "International / Destination" : "India-based event"}
                          </div>
                        </div>
                        <Globe2 size={20} className="text-[#d4af37]" />
                      </button>
                    </Field>
                  </div>

                  <div className="border-t border-white/10 pt-8">
                    <Field
                      label="Tell us about the celebration"
                      hint="Ritual flow, favourite music, planner notes, venue timing, mood"
                    >
                      <textarea
                        data-testid="input-message"
                        rows={5}
                        value={form.message}
                        onChange={update("message")}
                        className={`${inputCls} resize-none border border-white/10 bg-white/[0.02] px-4 py-4 text-sm leading-relaxed md:text-base`}
                        placeholder="For example: baraat entry at 6 PM, live dhol + shehnai for varmala, elegant reception stage with classic Bollywood and sufi notes..."
                      />
                    </Field>
                  </div>

                  <div className="flex flex-col gap-4 border-t border-white/10 pt-8">
                    <button
                      type="submit"
                      data-testid="booking-submit-button"
                      disabled={submitting}
                      className="gold-button inline-flex w-full items-center justify-center gap-2 rounded-sm py-5 text-xs font-semibold uppercase tracking-[0.3em] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={16} className="animate-spin" /> Sending enquiry
                        </>
                      ) : (
                        <>
                          Request Proposal <ArrowRight size={14} />
                        </>
                      )}
                    </button>

                    <div className="flex flex-col justify-between gap-3 text-[11px] tracking-wide text-white/40 md:flex-row md:items-center">
                      <div>
                        By submitting, you agree to be contacted by AB Events
                        regarding your enquiry.
                      </div>
                      <div className="inline-flex items-center gap-2 uppercase tracking-[0.26em] text-[#d4af37]">
                        <Sparkles size={14} /> Personal response within 24 hours
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const inputCls =
  "w-full border border-white/10 bg-white/[0.02] px-4 py-4 text-base font-light text-white outline-none transition-colors placeholder:text-white/28 focus:border-[#d4af37] focus:ring-0";

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-white/50">
      {label}
    </span>
    {children}
    {hint ? (
      <span className="mt-2 block text-[11px] leading-relaxed text-white/35">
        {hint}
      </span>
    ) : null}
  </label>
);

const ChoiceCard = ({ selected, title, note, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`min-h-[126px] border p-5 text-left transition-all duration-300 ${
      selected
        ? "border-[#d4af37] bg-[#d4af37]/10 shadow-[0_20px_60px_-30px_rgba(212,175,55,0.45)]"
        : "border-white/10 bg-black/20 hover:border-[#d4af37]/45 hover:bg-white/[0.03]"
    }`}
  >
    <div className="text-[10px] uppercase tracking-[0.28em] text-[#d4af37]">
      Signature format
    </div>
    <div className="mt-3 font-serif text-2xl font-light text-white">{title}</div>
    <p className="mt-2 text-sm font-light leading-relaxed text-white/55">{note}</p>
  </button>
);

const ChoicePill = ({ selected, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-full border px-4 py-2.5 text-sm transition-all duration-300 ${
      selected
        ? "border-[#d4af37] bg-[#d4af37]/12 text-white"
        : "border-white/10 bg-black/20 text-white/65 hover:border-[#d4af37]/45 hover:text-white"
    }`}
  >
    {children}
  </button>
);

const QuickAction = ({
  href,
  icon: Icon,
  eyebrow,
  title,
  detail,
  testId,
  accent = "",
  target,
  rel,
}) => (
  <a
    href={href}
    target={target}
    rel={rel}
    data-testid={testId}
    className={`group flex items-center justify-between border px-5 py-4 transition-all duration-300 ${
      accent ||
      "border-white/10 bg-white/[0.03] text-white hover:border-[#d4af37]/55 hover:bg-white/[0.05]"
    }`}
  >
    <div className="min-w-0">
      <div
        className={`text-[10px] uppercase tracking-[0.3em] ${
          accent ? "text-white/70" : "text-[#d4af37]"
        }`}
      >
        {eyebrow}
      </div>
      <div className="mt-2 truncate font-serif text-2xl font-light">{title}</div>
      <div className={`mt-1 text-sm ${accent ? "text-white/80" : "text-white/50"}`}>
        {detail}
      </div>
    </div>
    <div
      className={`ml-4 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition-transform duration-300 group-hover:translate-x-1 ${
        accent ? "border-white/20" : "border-[#d4af37]/30 text-[#d4af37]"
      }`}
    >
      <Icon size={18} />
    </div>
  </a>
);

const MiniTrust = ({ icon: Icon, title, detail }) => (
  <div className="border border-white/10 bg-white/[0.03] p-4">
    <div className="flex items-start gap-3">
      <div className="mt-0.5 text-[#d4af37]">
        <Icon size={16} />
      </div>
      <div>
        <div className="font-medium text-white">{title}</div>
        <div className="mt-1 text-sm font-light leading-relaxed text-white/50">
          {detail}
        </div>
      </div>
    </div>
  </div>
);

export default Bookings;
